import { AnyObjectSchema, object, string } from 'yup'
import type { ProjectFieldDTO } from '@api/fastAPI.schemas'
import { MAX_TEXT_VALUE_LENGTH } from 'src/components/manager/projectFields/utils'

const REQUIRED = 'Pflichtfeld'

/** The form path of a field's value. */
export const customFieldName = (field: ProjectFieldDTO): string =>
  `custom_field_values.${field.guid}`

const optionIds = (field: ProjectFieldDTO): string[] =>
  field.options.map(option => option.id).filter((id): id is string => !!id)

/**
 * The project schema plus one required entry per organisation field. A select only accepts one of
 * its current options, so a project whose option was deleted must pick again. Values of fields that
 * no longer exist stay in the object untouched.
 */
export const withCustomFields = <T extends AnyObjectSchema>(
  schema: T,
  fields: ProjectFieldDTO[],
): AnyObjectSchema =>
  fields.length
    ? schema.concat(
        object({
          custom_field_values: object(
            Object.fromEntries(
              fields.map(field => [
                field.guid,
                field.field_type === 'select'
                  ? string().required(REQUIRED).oneOf(optionIds(field), REQUIRED)
                  : string()
                      .trim()
                      .required(REQUIRED)
                      .max(MAX_TEXT_VALUE_LENGTH, `Maximal ${MAX_TEXT_VALUE_LENGTH} Zeichen`),
              ]),
            ),
          ),
        }),
      )
    : schema

/**
 * The form's starting values: what the project stored, with every field present -- an unfilled one
 * (or a select whose option is gone) starts empty so the required check catches it.
 */
export const customFieldDefaults = (
  fields: ProjectFieldDTO[],
  stored: Record<string, string> = {},
): Record<string, string> => ({
  ...stored,
  ...Object.fromEntries(
    fields.map(field => {
      const value = stored[field.guid] ?? ''
      const isValid = field.field_type !== 'select' || optionIds(field).includes(value)
      return [field.guid, isValid ? value : '']
    }),
  ),
})
